/**
 * see https://auth0.com/docs/quickstart/spa/react/01-login#install-the-auth0-react-wrapper
 */
import React, { useContext, useState } from "react";
import configuration from "../utilities/configuration";
import global from "../utilities/global";
import localStorage from "../utilities/localStorage";
import createAuthClient from "./authClient";
import { REFRESH_TOKEN } from "./authClient/src/constants";
import { isUsedOpenAM } from "../utilities/userUtils";
import { LOGIN_REDIRECT } from "./authClient/src/constants";

export const AuthContext = React.createContext(undefined);

export const useAuthContext = () => useContext(AuthContext);

export const AuthProvider = (
    {
        children,
        ...initOptions
    }) => {

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState();
    const [token, setToken] = useState();
    const [authClientRef, setAuthClientRef] = useState();
    const [error, setError] = useState("");

    const requestAuth = async () => {
        localStorage.setItem(LOGIN_REDIRECT, window.location.pathname);

        const authClient = global.get("authClient");

        // check if we need to start a login to AM or parse the response from AM
        if (!window.location.search.includes("code=")) {
            // do the login .. redirects to AM
            authClient.loginWithRedirect({})
                .catch(e => {
                    console.error(`loginWithRedirect Error: ${e.message}`);
                });
        }
    };

    const initAuth = async () => {
        // create the authClient
        const authClient = await createAuthClient({
            issuer: configuration.authIssuer,
            authEndpoint: configuration.authServerUrl + configuration.authEndpoint,
            tokenEndpoint: configuration.authServerUrl + configuration.authTokenEndpoint,
            logoutEndpoint: configuration.authServerUrl + configuration.authLogoutEndpoint,
            redirect_uri: configuration.authRedirectUri,
            logout_uri: configuration.authLogoutUri,
            client_id: configuration.authClientId,
            authProvider: configuration.authProvider,
            scope: "openid profile" + (isUsedOpenAM() && configuration.authGroup ? " " + configuration.authGroup : ""),
            requiredRole: configuration.authRole,
            ...initOptions
        });
        // save authClient for further use
        setAuthClientRef(authClient);
        global.set("authClient", authClient);

        // check if we need to start a login to AM or parse the response from AM
        if (window.location.search.includes("code=")) {
            // parse the response (URL parameters) from AM
            try {
                await authClient.handleRedirectCallback();
                setUser(authClient.getUser());
                setToken(authClient.getDecodedToken());
                setIsAuthenticated(authClient.isAuthenticated());
                setIsLoading(false);
            } catch (e) {
                setError(e.message);
                setIsLoading(false);
                if (e.message && e.message.indexOf("Invalid state") > -1) {
                    // invalid state, start from the beginning
                    window.location.replace(window.location.origin);
                    return;
                }
                console.error(`AuthProvider Error: ${e.message}`);
            }
        } else if (localStorage.getItem(REFRESH_TOKEN)) {
            try {
                await authClient.handleRefreshOnInit();
                setUser(authClient.getUser());
                setIsAuthenticated(authClient.isAuthenticated());
                setIsLoading(false);
            } catch (e) {
                if (e.message && e.message.indexOf("Invalid state") > -1) {
                    // invalid state, start from the beginning
                    window.location.replace(window.location.origin);
                    return;
                }
                console.error(`AuthProvider Error: ${e.message}`);
            }
        }
    };

    const refreshAuth = async () => {
        await authClientRef?.handleRefreshOnInit();
    };

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                isLoading,
                user,
                token,
                error,
                handleRedirectCallback: (...p) => authClientRef.handleRedirectCallback(...p),
                loginWithRedirect: (...p) => authClientRef.loginWithRedirect(...p),
                handleRefreshOnInit: (...p) => authClientRef.handleRefreshOnInit(...p),
                logoutWithRedirect: (...p) => {
                    authClientRef.logout(...p); // returnTo: configuration.authPostLogoutUriSelector
                },
                requestAuth,
                initAuth,
                refreshAuth,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
